// Import core React and React hooks
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Import third-party libraries
import { AutoComplete, AutoCompleteProps } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { debounce } from 'lodash';

// Import Google Maps hooks
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_API_KEY } from 'utils/envUtil';

// Import our own components and utilities

type EZAutocompleteAddressProps = AutoCompleteProps<any> & {
    ifEmptyText?: string;
    country?: string | string[];
};

type AddressDetails = {
    place_id?: string;

    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
};

export type EZAutocompleteAddressOption = DefaultOptionType & AddressDetails;

export const EZAutocompleteAddress: React.FC<EZAutocompleteAddressProps> = ({
    ifEmptyText = '-',
    country = 'us',
    onSelect,
    ...props
}) => {
    const [addressSearchOptions, setAddressSearchOptions] = useState<EZAutocompleteAddressOption[]>(
        []
    );
    const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);

    // 1) Load the Maps+Places JS API once
    useEffect(() => {
        const loader = new Loader({ apiKey: GOOGLE_MAPS_API_KEY });
        Promise.all([loader.importLibrary('maps'), loader.importLibrary('places')])
            .then(([mapsNs, placesNs]) => {
                // mapsNs === window.google.maps
                // placesNs === window.google.maps.places
                autocompleteServiceRef.current = new placesNs.AutocompleteService();
            })
            .catch(console.error);
    }, []);

    // Function to call Google Places Autocomplete API
    const fetchPredictions = (searchKeyword: string) => {
        if (!autocompleteServiceRef.current || !searchKeyword) {
            setAddressSearchOptions([]);
            return;
        }

        autocompleteServiceRef.current.getPlacePredictions(
            {
                input: searchKeyword,
                types: ['address'],
                componentRestrictions: { country: country },
            },
            (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
                    setAddressSearchOptions(
                        predictions.map(p => ({
                            label: p.description,
                            // value: p.structured_formatting?.main_text || p.description,
                            value: p.description,
                            street: p.structured_formatting?.main_text,
                            place_id: p.place_id,
                            key: p.place_id,
                        }))
                    );
                } else {
                    setAddressSearchOptions([]);
                }
            }
        );
    };

    // Debounce search to avoid too many API calls
    const _onSearchAddress = useCallback(
        debounce((searchKeyword: string) => {
            fetchPredictions(searchKeyword);
        }, 500),
        []
    );

    const _onSelectAddress = (value: string, option: EZAutocompleteAddressOption) => {
        const addressDetails: AddressDetails = {
            street: option.street,
            city: '',
            state: '',
            zip: '',
            country: 'US',
        };

        if (option.place_id) {
            // get full details for city/state/postal
            const mapDiv = document.createElement('div');
            const placesSvc = new window.google.maps.places.PlacesService(mapDiv);
            placesSvc.getDetails(
                {
                    placeId: option.place_id,
                    fields: ['address_components', 'formatted_address'],
                },
                (place, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                        const comps = place.address_components || [];
                        const findComp = (type: string, short = false) =>
                            comps.find(c => c.types.includes(type))?.[
                                short ? 'short_name' : 'long_name'
                            ] ?? '';

                        const city = findComp('locality');
                        const stateCode = findComp('administrative_area_level_1', true);
                        const postalCode = findComp('postal_code');

                        addressDetails.city = city;
                        addressDetails.state = stateCode;
                        addressDetails.zip = postalCode;

                        onSelect?.(value, {
                            ...option,
                            ...addressDetails,
                        });
                    } else {
                        onSelect?.(value, option);
                    }
                }
            );
        } else {
            onSelect?.(value, option);
        }
    };

    return (
        <AutoComplete
            allowClear
            options={addressSearchOptions}
            onSearch={_onSearchAddress}
            placeholder="Enter the property address"
            onSelect={_onSelectAddress}
            dropdownRender={menu => (
                <>
                    {menu}
                    <div
                        style={{
                            padding: '8px',
                            borderTop: '1px solid #f0f0f0',
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            💡 Tip: Include ZIP code for more accurate results
                        </div>
                        <div>
                            Powered by{' '}
                            <img
                                alt="Powered by Google"
                                src="/attributions/GoogleMaps_Logo_DarkGray_1x.png"
                                style={{ height: 18, borderRadius: 0 }}
                            />
                        </div>
                    </div>
                </>
            )}
            {...props}
        />
    );
};
